import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { take } from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import { LookupValue } from "../../models/common/lookup-value";
import { Checkpoint } from "../../models/community-bereavement/checkpoint";
import { Member } from "../../models/community-bereavement/member";
import { MemberRisk } from "../../models/community-bereavement/member-risk";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import { CBMemberData } from "../../navigation/components/brief-bar/cb-member-data";
import { ConfigurationService } from "../configuration/configuration.service";

/*
  Generated class for the CommunityBereavementServiceProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class CommunityBereavementServiceProvider {
  apiUrl: string;
  isMemberDataLoaded: Subject<any> = new Subject<any>();

  constructor(public http: HttpClient, private config: ConfigurationService) {
    if (config.getConfig().isLocal) {
      this.apiUrl =
        config.getConfig().localEndpoints.communityBereaved +
        "/community-bereaved";
    } else {
      this.apiUrl = config.getConfig().apiUrl + "/community-bereaved";
    }
  }

  getLookUpValues() {
    return this.http
      .get<ContentWrapper<LookupValue>>(`${this.apiUrl}/lookups`)
      .pipe(take(1));
  }

  getCbMembers(
    page = 0,
    size = 10,
    orderBy = "createdDateTime",
    sortBy = "desc"
  ): Observable<ContentWrapper<Member>> {
    return this.http
      .get<ContentWrapper<Member>>(
        `${this.apiUrl}/members?page=${page}&size=${size}&direction=${orderBy}&sortBy=${sortBy}`
      )
      .pipe(take(1));
  }

  getCbMemberById(memberId: number) {
    return new Observable((observer) => {
      this.http.get<Member>(`${this.apiUrl}/${memberId}`).subscribe(
        (data) => {
          observer.next(data);
          this.setIsMemberDataLoaded(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  createMember(data: any) {
    return this.http.post(`${this.apiUrl}`, data).pipe(take(1));
  }

  updateMember(memberId: number, data) {
    return this.http
      .put<Member>(`${this.apiUrl}/${memberId}`, data)
      .pipe(take(1));
  }

  //@TODO: SMS - We may be able to remove this if we don't need to pull brief bar data from back-end
  getMemberBrief(memberId: number) {
    return this.http
      .get<CBMemberData>(`${this.apiUrl}/memberBrief`)
      .pipe(take(1));
  }

  changeCBStatus(memberID, status) {
    return this.http
      .put<Member>(`${this.apiUrl}/members/${memberID}/status/${status}`, null)
      .pipe(take(1));
  }

  getRisksByMember(memberId: number) {
    return this.http
      .get<MemberRisk[]>(`${this.apiUrl}/${memberId}/risks`)
      .pipe(take(1));
  }

  getMemberRiskCheckpoints(memberId: number, memberRiskId: number) {
    return this.http
      .get<Checkpoint[]>(
        `${this.apiUrl}/${memberId}/risks/${memberRiskId}/checkpoints`
      )
      .pipe(take(1));
  }

  createMemberRiskCheckpoint(
    memberId: number,
    memberRiskId: number,
    data: any
  ) {
    return this.http
      .post(
        `${this.apiUrl}/${memberId}/risks/${memberRiskId}/checkpoints`,
        data
      )
      .pipe(take(1));
  }

  updateMemberRiskCheckpoint(
    memberId: number,
    memberRiskId: number,
    checkpointId: number,
    data: any
  ) {
    return this.http
      .put<Checkpoint>(
        `${this.apiUrl}/${memberId}/risks/${memberRiskId}/checkpoints/${checkpointId}`,
        data
      )
      .pipe(take(1));
  }

  setIsMemberDataLoaded(member: Member) {
    if (member) {
      this.isMemberDataLoaded.next(member);
    }
  }

  addCBRisks(
    memberId: number,
    data: any,
    memberRisk: MemberRisk,
    action: string
  ) {
    if (action.toLowerCase() === "edit") {
      return this.http
        .put(`${this.apiUrl}/${memberId}/risks/${memberRisk.id}`, data)
        .pipe(take(1));
    } else {
      return this.http
        .post(`${this.apiUrl}/${memberId}/risks`, data)
        .pipe(take(1));
    }
  }

  deleteCBRisks(memberId: number, memberRiskId: number) {
    return this.http
      .delete(`${this.apiUrl}/${memberId}/risks/${memberRiskId}`)
      .pipe(take(1));
  }

  deleteCBCheckpoint(
    checkpointId: number,
    memberRiskId: number,
    memberId: number
  ) {
    return this.http
      .delete(
        `${this.apiUrl}/${memberId}/risks/${memberRiskId}/checkpoints/${checkpointId}`
      )
      .pipe(take(1));
  }
}



// WEBPACK FOOTER //
// ./src/providers/community-bereavement-service/community-bereavement-service.ts